import { cn } from "@/lib/utils.ts";
import * as React from "react";

interface ToggleProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  leftLabel?: string;
  rightLabel?: string;
  /** `sm` is a compact switch for tight rows (e.g. inline section toggles). */
  /** Derived from `toggleSizes`, so adding a size can't leave this behind. */
  size?: ToggleSize;
  /** Lets an external `<label htmlFor>` activate the switch on click. */
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

const toggleSizes = {
  default: { track: "h-6 w-11", knob: "h-4 w-4", on: "translate-x-6", off: "translate-x-1" },
  // Between `default` and `sm`, for a switch sitting on a card beside the
  // 24px status pills: `default` matches those pills in height but its 44px
  // track outweighs a card, while `sm` is half their height. 20x36 reads level
  // with a pill without dominating the card, and clears the 24px pointer
  // target on its own.
  md: {
    track: "h-5 w-9",
    knob: "h-3.5 w-3.5",
    on: "translate-x-[19px]",
    off: "translate-x-[3px]",
  },
  sm: {
    // Visual track stays 21×12; a transparent centered ::before extends the
    // pointer hit target to 24×24 (WCAG 2.5.8) without enlarging the switch.
    track:
      "h-3 w-[21px] before:absolute before:left-1/2 before:top-1/2 before:size-6 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']",
    knob: "h-[9px] w-[9px]",
    on: "translate-x-[11px]",
    off: "translate-x-[1px]",
  },
} as const;

type ToggleSize = keyof typeof toggleSizes;

const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      checked,
      onCheckedChange,
      disabled,
      className,
      leftLabel,
      rightLabel,
      size = "default",
      id,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
    },
    ref,
  ) => {
    const s = toggleSizes[size];
    return (
      <div className="flex items-center space-x-3">
        {leftLabel && (
          <span className="text-sm font-medium text-muted-foreground">{leftLabel}</span>
        )}
        <button
          ref={ref}
          id={id}
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          disabled={disabled}
          onClick={() => onCheckedChange(!checked)}
          className={cn(
            "relative inline-flex cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            s.track,
            checked ? "bg-primary" : "bg-muted-foreground/30",
            className,
          )}
        >
          <span
            className={cn(
              "pointer-events-none block transform rounded-full bg-background shadow-lg ring-0 transition-transform",
              s.knob,
              checked ? s.on : s.off,
            )}
          />
        </button>
        {rightLabel && (
          <span className="text-sm font-medium text-muted-foreground">{rightLabel}</span>
        )}
      </div>
    );
  },
);

Toggle.displayName = "Toggle";

export { Toggle };
